import {
    type CompanyScale,
    type CompetitorEntry,
    parseCompetitorsByScale,
    parseCompetitorsFromEnrich
} from "./competitorConstants";
import {
    circleFlagUrl,
    COUNTRY_OPTIONS,
    countryIsoCodeFromValue,
    countryMeta,
    type CountryOption,
    DEFAULT_LOCATION_COUNTRY
} from "./countryOptions";

export type ICPTargetLocation = Record<string, string[]>;

export type SellingType = "product-led" | "sales-led" | "hybrid";

export {
    circleFlagUrl,
    COUNTRY_OPTIONS,
    countryIsoCodeFromValue,
    countryMeta,
    type CountryOption,
    DEFAULT_LOCATION_COUNTRY
};

export const SELLING_TYPE_OPTIONS: { value: SellingType; label: string }[] = [
    { value: "product-led", label: "Product-led" },
    { value: "sales-led", label: "Sales-led" },
    { value: "hybrid", label: "Hybrid" }
];

const GENERIC_ICP_KEYS = new Set([
    "startups",
    "startup",
    "smb",
    "smbs",
    "mid_market",
    "mid-market",
    "enterprise",
    "enterprises",
    "b2b",
    "b2c"
]);

export function icpSegmentKeys(map: ICPTargetLocation | undefined): string[] {
    if (!map) return [];
    return Object.keys(map).filter((key) => key.trim() !== "" && !GENERIC_ICP_KEYS.has(key.toLowerCase()));
}

export function primaryIcpKey(map: ICPTargetLocation | undefined): string {
    const keys = icpSegmentKeys(map);
    if (keys.length === 0) return "";
    const withCountries = keys.find((key) => (map?.[key]?.length ?? 0) > 0);
    return withCountries ?? keys[0];
}

export function countriesForIcp(map: ICPTargetLocation | undefined, icpKey: string): string[] {
    return map?.[icpKey]?.slice() ?? [];
}

export function updateIcpCountries(map: ICPTargetLocation, icpKey: string, countries: string[]): ICPTargetLocation {
    return { ...map, [icpKey]: countries };
}

export function normalizeSellingType(value: string | undefined): SellingType | "" {
    const normalized = (value || "").toLowerCase().trim();
    if (normalized === "product-led" || normalized === "sales-led" || normalized === "hybrid") {
        return normalized;
    }
    return "";
}

export function matchIndustry(value: string | undefined): string {
    return (value || "").trim();
}

export type EnrichedBrandProfile = {
    industry: string;
    selectedIcp: string;
    icpTargetLocation: ICPTargetLocation;
    sellingType: SellingType | "";
};

export function applyEnrichedBrandProfile(data: {
    industry?: string;
    icp_target_location?: ICPTargetLocation;
    selling_type?: string;
}): EnrichedBrandProfile {
    const icpTargetLocation =
        data.icp_target_location && icpSegmentKeys(data.icp_target_location).length > 0 ? data.icp_target_location : {};
    const selectedIcp = primaryIcpKey(icpTargetLocation);
    return {
        industry: matchIndustry(data.industry),
        selectedIcp,
        icpTargetLocation,
        sellingType: normalizeSellingType(data.selling_type) || ""
    };
}

// ── Target markets ────────────────────────────────────────────────────────────

/** Map an ISO 3166-1 alpha-2 code (case-insensitive) to its country name. */
export function countryNameFromCode(code: string): string {
    const norm = (code || "").trim().toLowerCase();
    return COUNTRY_OPTIONS.find((o) => o.code.toLowerCase() === norm)?.value ?? code.toUpperCase();
}

/** Map a country name or ISO code to a normalised uppercase ISO code (e.g. "United States" → "US"). */
export function countryCodeFromName(nameOrCode: string): string {
    const trimmed = (nameOrCode || "").trim();
    if (!trimmed) return "";
    const byName = COUNTRY_OPTIONS.find((o) => o.value.toLowerCase() === trimmed.toLowerCase());
    if (byName) return byName.code.toUpperCase();
    const byCode = COUNTRY_OPTIONS.find((o) => o.code.toLowerCase() === trimmed.toLowerCase());
    if (byCode) return byCode.code.toUpperCase();
    return trimmed.toUpperCase();
}

/** Convert API target_markets (ISO codes) to display country names. */
export function targetMarketsFromApi(codes: string[] | undefined): string[] {
    return (codes || []).filter(Boolean).slice(0, 6).map(countryNameFromCode);
}

/** Convert UI country names back to ISO codes for the API. */
export function targetMarketsToApi(names: string[]): string[] {
    return names.filter(Boolean).map(countryCodeFromName);
}

// ── Structured target markets (geo) ───────────────────────────────────────────

export type TargetMarketGeo = {
    market_size: string;
    country_code: string;
    city_name?: string;
    region_name?: string;
    place_ref?: string;
    lat?: number;
    lon?: number;
    is_primary?: boolean;
    source?: string;
    confidence?: number;
};

export function targetMarketsGeoFromApi(rows: TargetMarketGeo[] | undefined): TargetMarketGeo[] {
    if (!rows?.length) return [];
    return rows
        .map((r) => ({
            market_size: (r.market_size || "national").toLowerCase(),
            country_code: (r.country_code || "").toUpperCase(),
            city_name: r.city_name?.trim() || undefined,
            region_name: r.region_name?.trim() || undefined,
            place_ref: r.place_ref?.trim() || undefined,
            lat: typeof r.lat === "number" ? r.lat : undefined,
            lon: typeof r.lon === "number" ? r.lon : undefined,
            is_primary: r.is_primary,
            source: r.source,
            confidence: r.confidence
        }))
        .filter((r) => r.country_code.length === 2);
}

/** Legacy country codes → national geo rows when geo is empty. */
export function nationalMarketsFromCountryCodes(codes: string[] | undefined): TargetMarketGeo[] {
    const seen = new Set<string>();
    const out: TargetMarketGeo[] = [];
    for (const raw of codes || []) {
        const cc = countryCodeFromName(raw);
        if (cc.length !== 2 || seen.has(cc)) continue;
        seen.add(cc);
        out.push({ market_size: "national", country_code: cc, source: "user_added" });
    }
    return out;
}

export function countryProjectionFromGeo(markets: TargetMarketGeo[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const m of markets) {
        const cc = (m.country_code || "").toUpperCase();
        if (cc.length !== 2 || seen.has(cc)) continue;
        seen.add(cc);
        out.push(cc);
    }
    return out;
}

// ── Audience distribution ─────────────────────────────────────────────────────

export type AudienceSegment = {
    id: string;
    label: string;
    description: string;
    enabled: boolean;
    percent: number;
};

/** The three buyer intents. Replaces the explorer/comparator/validator archetypes
 *  and the separate buyer-journey phase — one axis, per the Peec taxonomy. */
export const DEFAULT_AUDIENCE_SEGMENTS: AudienceSegment[] = [
    {
        id: "informational",
        label: "Informational",
        description: "Still learning the category, not comparing options yet.",
        enabled: true,
        percent: 20
    },
    {
        id: "commercial",
        label: "Commercial",
        description: "Knows the category, weighing options against each other.",
        enabled: true,
        percent: 30
    },
    {
        id: "transactional",
        label: "Transactional",
        description: "Close to deciding, clearing final blockers to act.",
        enabled: true,
        percent: 50
    }
];

export function audiencePercentTotal(segments: AudienceSegment[]): number {
    return segments.filter((s) => s.enabled).reduce((sum, s) => sum + s.percent, 0);
}

export function isAudienceDistributionValid(segments: AudienceSegment[]): boolean {
    const enabled = segments.filter((s) => s.enabled);
    if (enabled.length === 0) return false;
    return audiencePercentTotal(segments) === 100;
}

export function brandNameFromDomain(domain: string): string {
    const host = domain.replace(/^www\./i, "").split(".")[0] || "";
    if (!host) return "";
    return host.charAt(0).toUpperCase() + host.slice(1);
}

export function tagsFromValueOffering(value: string | undefined): string[] {
    if (!value?.trim()) return [];
    return value
        .split(/[,;•\n]|(?:\s+and\s+)/i)
        .map((part) => part.trim())
        .filter((part) => part.length > 0 && part.length <= 48);
}

// ── Domain enrich API → UI state ─────────────────────────────────────────────

/** A generated AI-visibility topic. `/domains/enrich` returns this shape; older
 * cached responses (or domain_preferences-backed callers) may still send a
 * plain string, so callers should go through `topicNameFromApiTopic`. */
export type EnrichedAiTopic =
    | string
    | {
          topic: string;
          rationale?: string;
          recommended?: boolean;
      };

export function topicNameFromApiTopic(entry: EnrichedAiTopic): string {
    if (typeof entry !== "string") return entry.topic ?? "";
    const raw = entry.trim();
    // Prefs / legacy rows sometimes store encoded TopicItem JSON from domain_context seeding.
    if (raw.startsWith("{")) {
        try {
            const parsed = JSON.parse(raw) as { topic?: string };
            if (parsed?.topic) return String(parsed.topic).trim();
        } catch {
            /* plain string that happens to start with { */
        }
    }
    return raw;
}

export function isRecommendedApiTopic(entry: EnrichedAiTopic): boolean {
    return typeof entry !== "string" && entry.recommended === true;
}

export type EnrichedDomainContextApi = {
    description?: string;
    industry?: string;
    value_offering?: string;
    competitors?: string[];
    ai_topics?: EnrichedAiTopic[];
    hq_country_code?: string;
    brand_identity?: string[];
    products_and_services?: string[];
    target_markets?: string[];
    intent_informational?: number;
    intent_commercial?: number;
    intent_transactional?: number;
    occupation?: string;
    business_model?: string;
    customer_segments?: string[];
    company_scale?: string;
    competitors_by_scale?: {
        recommended_scale?: string;
        small?: string[];
        medium?: string[];
        large?: string[];
        legacy?: string[];
    };
    target_markets_geo?: TargetMarketGeo[];
};

export type ParsedEnrichedDomainContext = {
    companyDescription: string;
    valueOffering: string;
    aiTopics: string[];
    /** Subset of aiTopics AI-ranked as the top picks — pre-select these, not the full list. */
    recommendedTopics: string[];
    competitors: string[];
    competitorDetails: CompetitorEntry[];
    brandIndustry: string;
    brandIdentity: string[];
    productsAndServices: string[];
    targetMarkets: string[];
    targetMarketsGeo: TargetMarketGeo[];
    audienceSegments: AudienceSegment[];
    brandCountry: string;
    occupation: string;
    /** Passed through untouched from enrich → preferences so the backend keeps them. */
    businessModel: string;
    customerSegments: string[];
    companyScale: CompanyScale;
    competitorsByScale: Record<CompanyScale, CompetitorEntry[]>;
    hasScaleCatalog: boolean;
};

const AUDIENCE_ENRICH_KEYS: { id: string; key: keyof EnrichedDomainContextApi }[] = [
    { id: "informational", key: "intent_informational" },
    { id: "commercial", key: "intent_commercial" },
    { id: "transactional", key: "intent_transactional" }
];

export function countryValueFromIsoCode(code: string | undefined): string | undefined {
    const trimmed = (code || "").trim();
    if (!trimmed) return undefined;
    const byName = COUNTRY_OPTIONS.find((o) => o.value.toLowerCase() === trimmed.toLowerCase());
    if (byName) return byName.value;
    return COUNTRY_OPTIONS.find((o) => o.code === trimmed.toLowerCase())?.value;
}

function audienceSegmentsFromEnrich(data: EnrichedDomainContextApi): AudienceSegment[] {
    const percents = AUDIENCE_ENRICH_KEYS.map(({ id, key }) => ({
        id,
        percent: typeof data[key] === "number" ? data[key] : undefined
    }));
    const hasAny = percents.some((p) => p.percent !== undefined);
    const total = percents.reduce((sum, p) => sum + (p.percent ?? 0), 0);
    if (!hasAny || total !== 100) {
        return DEFAULT_AUDIENCE_SEGMENTS.map((s) => ({ ...s }));
    }
    return DEFAULT_AUDIENCE_SEGMENTS.map((template) => {
        const match = percents.find((p) => p.id === template.id);
        const percent = match?.percent ?? 0;
        return { ...template, enabled: percent > 0, percent };
    });
}

export function defaultBrandProfileState(): Pick<
    ParsedEnrichedDomainContext,
    | "brandIndustry"
    | "brandIdentity"
    | "productsAndServices"
    | "targetMarkets"
    | "targetMarketsGeo"
    | "audienceSegments"
    | "brandCountry"
> {
    return {
        brandIndustry: "",
        brandIdentity: [],
        productsAndServices: [],
        targetMarkets: [],
        targetMarketsGeo: [],
        audienceSegments: DEFAULT_AUDIENCE_SEGMENTS.map((s) => ({ ...s })),
        brandCountry: DEFAULT_LOCATION_COUNTRY
    };
}

export function audienceSegmentLabel(id: string, segments: AudienceSegment[] = DEFAULT_AUDIENCE_SEGMENTS): string {
    return segments.find((s) => s.id === id)?.label ?? id;
}

function weightedIdSequence<T extends string>(count: number, items: { id: T; weight: number }[]): T[] {
    if (count <= 0) return [];
    const active = items.filter((item) => item.weight > 0);
    if (active.length === 0) return Array(count).fill(items[0]?.id).filter(Boolean) as T[];

    const total = active.reduce((sum, item) => sum + item.weight, 0);
    const sequence: T[] = [];
    const quotas = active.map((item) => ({
        id: item.id,
        count: Math.max(0, Math.round((item.weight / total) * count))
    }));

    let assigned = quotas.reduce((sum, q) => sum + q.count, 0);
    while (assigned < count) {
        const next = quotas.sort((a, b) => b.count - a.count)[0];
        next.count += 1;
        assigned += 1;
    }
    while (assigned > count) {
        const next = quotas.sort((a, b) => a.count - b.count).find((q) => q.count > 0);
        if (!next) break;
        next.count -= 1;
        assigned -= 1;
    }

    for (const quota of quotas) {
        for (let i = 0; i < quota.count; i++) sequence.push(quota.id);
    }
    return sequence.slice(0, count);
}

export type PromptMetadataInput = {
    audienceSegmentId?: string;
};

export function assignMetadataToPrompts<T extends Record<string, unknown>>(
    prompts: T[],
    audienceSegments: AudienceSegment[]
): Array<T & { audienceSegmentId: string }> {
    const audienceSequence = weightedIdSequence(
        prompts.length,
        audienceSegments
            .filter((segment) => segment.enabled)
            .map((segment) => ({ id: segment.id, weight: segment.percent }))
    );

    return prompts.map((prompt, index) => ({
        ...prompt,
        audienceSegmentId: audienceSequence[index] ?? audienceSegments[0]?.id
    }));
}

export function parseEnrichedDomainContext(data: EnrichedDomainContextApi): ParsedEnrichedDomainContext | null {
    const companyDescription = (data.description || "").trim();
    if (!companyDescription) return null;

    const valueOffering = data.value_offering || "";
    const apiProducts = (data.products_and_services || []).filter(Boolean).slice(0, 8);
    const apiBrandIdentity = (data.brand_identity || []).filter(Boolean).slice(0, 8);

    const scaleCatalog = parseCompetitorsByScale(data);
    const competitorEntries =
        scaleCatalog.selected.length > 0 ? scaleCatalog.selected : parseCompetitorsFromEnrich(data);
    const apiTopics = data.ai_topics || [];
    const aiTopics = apiTopics.map(topicNameFromApiTopic).filter(Boolean);
    const recommendedTopics = apiTopics.filter(isRecommendedApiTopic).map(topicNameFromApiTopic).filter(Boolean);
    return {
        companyDescription,
        valueOffering,
        aiTopics,
        recommendedTopics,
        competitors: competitorEntries.map((c) => c.domain),
        competitorDetails: competitorEntries,
        brandIndustry: matchIndustry(data.industry),
        brandIdentity: apiBrandIdentity,
        productsAndServices: apiProducts.length > 0 ? apiProducts : tagsFromValueOffering(valueOffering).slice(0, 8),
        targetMarkets: targetMarketsFromApi(data.target_markets),
        targetMarketsGeo: (() => {
            const geo = targetMarketsGeoFromApi(data.target_markets_geo);
            if (geo.length > 0) return geo;
            return nationalMarketsFromCountryCodes(data.target_markets);
        })(),
        audienceSegments: audienceSegmentsFromEnrich(data),
        brandCountry: countryValueFromIsoCode(data.hq_country_code) || DEFAULT_LOCATION_COUNTRY,
        occupation: (data.occupation || "").trim(),
        businessModel: (data.business_model || "").trim(),
        customerSegments: (data.customer_segments || []).filter(Boolean),
        companyScale: scaleCatalog.scale,
        competitorsByScale: scaleCatalog.byScale,
        hasScaleCatalog: scaleCatalog.hasCatalog
    };
}
